import React, { useState, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/native';
import moment from 'moment';
import Badge from '../badge';
import Text from '../text';
import Colors from '../colors';
import Spacer from '../spacer';
import Payment from './payment';
import useMobile from '../hooks/use-mobile';
import Icon from '../icon';
import LabelDataPair from '../label-data-pair';
import Alert from '../alert';
import Currency from '../utilities/currency';
const StyledContainer = styled.View`
  flex-direction: column;
  justify-content: space-between;
  background-color: ${Colors.white};
  border: ${props => props.expanded ? 1 : 0}px solid ${Colors.lightGray500};
  border-radius: 4px;
  width: 100%;
  filter: drop-shadow(0px 1px 5px rgba(0, 0, 0, 0.1));
`;
const StyledContentRow = styled.View`
  flex-direction: row;
  align-items: center;
`;
const StyledPressable = styled.Pressable`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-top: ${props => props.padding}px;
  padding-left: 14px;
  padding-bottom: ${props => props.expanded ? 0 : props.padding}px
  padding-right: 16px;
`;
const PaymentsContainer = styled.View`
  padding: 0px 16px 16px 16px;
`;

const formatDate = date => moment(date).format('MMM DD, YYYY');

const devices = {
  desktop: {
    containerPadding: 16,
    dueCaretSpacer: 'f20',
    dueBadgeSpacer: 'f12',
    stateCaretSpacer: 'f16',
    headerInfoSpacer: 'f20'
  },
  mobile: {
    containerPadding: 12,
    dueCaretSpacer: 'f16',
    dueBadgeSpacer: 'f8',
    stateCaretSpacer: 'f12',
    headerInfoSpacer: 'f12'
  }
};
const states = {
  paid: {
    label: 'Paid',
    icon: 'check-circle',
    color: Colors.success300
  },
  confirming: {
    label: 'Confirming',
    icon: 'pending',
    color: Colors.advanceBlue400,
    alert: {
      type: 'information',
      description: 'We are waiting for the confirmation of deductions from your HR'
    }
  },
  carryOver: {
    alert: {
      type: 'warning',
      description: 'You have payments that are past due.'
    }
  }
};

const getStateHeaderComponent = (paid, device) => {
  const state = paid ? states.paid : states.confirming;
  return /*#__PURE__*/React.createElement(StyledContentRow, null, /*#__PURE__*/React.createElement(Icon.MaterialIcons, {
    name: state.icon,
    color: state.color,
    size: 16
  }), /*#__PURE__*/React.createElement(Spacer, {
    size: "f6",
    width: true
  }), /*#__PURE__*/React.createElement(Text.BodySmall, {
    weight: "bold",
    color: state.color
  }, state.label), /*#__PURE__*/React.createElement(Spacer, {
    size: device.stateCaretSpacer,
    width: true
  }));
};

const getInformationComponent = confirming => {
  const alert = confirming ? states.confirming.alert : states.carryOver.alert;
  return /*#__PURE__*/React.createElement(Alert.Simple, {
    type: alert.type
  }, alert.description);
};

export default function Grouped(props) {
  const mobile = useMobile();
  const ref = useRef();
  const [expanded, setExpanded] = useState(false);
  const device = mobile ? devices.mobile : devices.desktop;
  const paidOrConfirming = props.paid || props.confirming;
  return /*#__PURE__*/React.createElement(StyledContainer, {
    expanded: expanded
  }, /*#__PURE__*/React.createElement(StyledPressable, {
    padding: device.containerPadding,
    expanded: expanded,
    ref: ref,
    onPress: () => setExpanded(!expanded)
  }, /*#__PURE__*/React.createElement(StyledContentRow, null, /*#__PURE__*/React.createElement(LabelDataPair, {
    type: "bottom-left",
    label: props.title
  }, Currency.pesoFormat(props.amount), !paidOrConfirming && props.carryOverCount > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacer, {
    size: device.dueBadgeSpacer,
    width: true
  }), /*#__PURE__*/React.createElement(Badge, {
    type: "warning"
  }, props.carryOverCount)))), /*#__PURE__*/React.createElement(StyledContentRow, null, paidOrConfirming && getStateHeaderComponent(props.paid, device) || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LabelDataPair, {
    type: "bottom-right",
    label: props.label
  }, formatDate(props.dueDate)), /*#__PURE__*/React.createElement(Spacer, {
    size: device.dueCaretSpacer,
    width: true
  })), /*#__PURE__*/React.createElement(Icon.MaterialIcons, {
    name: expanded ? 'expand-less' : 'expand-more',
    size: 24,
    color: Colors.gray600
  }))), expanded && /*#__PURE__*/React.createElement(PaymentsContainer, null, /*#__PURE__*/React.createElement(Spacer, {
    size: "f20",
    height: true
  }), (props.confirming || props.carryOverCount > 0) && /*#__PURE__*/React.createElement(React.Fragment, null, getInformationComponent(props.confirming), /*#__PURE__*/React.createElement(Spacer, {
    size: "f8",
    height: true
  })), props.payments.map((payment, index) => {
    const isLast = index === props.payments.length - 1;
    return /*#__PURE__*/React.createElement(Fragment, {
      key: index
    }, /*#__PURE__*/React.createElement(Payment, {
      icon: payment.icon,
      purpose: payment.purpose,
      paymentNo: payment.paymentNo,
      paymentCount: payment.paymentCount,
      amount: payment.amount,
      dueDate: payment.dueDate,
      carryOver: payment.carryOver,
      onPress: payment.onPress
    }), !isLast && /*#__PURE__*/React.createElement(Spacer, {
      size: "f8",
      height: true
    }));
  })));
}
Grouped.propTypes = {
  title: PropTypes.string,
  label: PropTypes.string,
  amount: PropTypes.number,
  dueDate: PropTypes.instanceOf(Date),
  confirming: PropTypes.bool,
  paid: PropTypes.bool,
  carryOverCount: PropTypes.number,
  payments: PropTypes.arrayOf(PropTypes.shape(Payment.propTypes))
};
Grouped.defaultProps = {
  title: 'Total Amount Due',
  label: 'Due On',
  amount: 0,
  dueDate: null,
  confirming: false,
  paid: false,
  carryOverCount: 0,
  payments: []
};
//# sourceMappingURL=grouped.js.map